import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Bell,
  BellOff,
  BellRing,
  Calendar,
  Check,
  ChevronLeft,
  Clock,
  Home as HomeIcon,
  Info,
  Moon,
  Settings as SettingsIcon,
  TriangleAlert,
  Volume2,
  VolumeX,
  Wrench,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { now } from '@/db/ids';
import type { NotificationFrequency } from '@/db/types';
import {
  NOTIFICATIONS_AVAILABLE,
  WEB_NOTIFICATIONS_AVAILABLE,
  cancelAllSystemReminders,
  cancelAllTaskReminders,
  composeAndScheduleDigest,
  dayLabel,
  fireConfirmationNotification,
  getNotificationPermissionStatus,
  isInQuietHours,
  requestNotificationPermissionUnified,
  showImmediateWebNotification,
  type NotificationPermissionStatus,
} from '@/lib/notifications';
import { goBackOrTo, goTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Push Notifications settings.
 *
 * Layout:
 *   1. Permission card — the most important thing on the page. Tells
 *      the user whether the OS has granted permission, and gives them
 *      one tap to fix it. Adapts copy + button for web vs native vs
 *      already-granted vs denied-permanently.
 *   2. Master switch — `notifications_enabled` global gate. When OFF,
 *      no notifications fire even if permission is granted.
 *   3. Digest controls — frequency + day + hour (existing). Only the
 *      day-of-week chip strip appears in weekly mode.
 *   4. Per-category toggles — overdue nudge today, more added as we
 *      ship them (achievements, weather, etc.).
 *
 * Cross-platform note: on web we use the foreground Notification API
 * via service worker. We can't schedule daily/weekly fires the way
 * native does, but real-time pings (achievement unlocks, anything that
 * happens while the tab is open) work and the inbox always carries
 * the persistent record.
 */

const FREQ_OPTIONS: { v: NotificationFrequency; label: string; sub: string }[] = [
  { v: 'off', label: 'Off', sub: 'No digest at all' },
  { v: 'daily', label: 'Daily', sub: "Today's one thing, every morning" },
  { v: 'weekly', label: 'Weekly', sub: 'A single digest on your chosen day' },
  { v: 'monthly', label: 'Monthly', sub: 'One check-in on the 1st of each month' },
];

// Hours offered for the quiet-hours from/until pickers. Covers all 24
// so the user can build any window (e.g. afternoon quiet for a night
// shift worker), but the defaults nudge toward the typical "sleep
// window" pattern (10pm–7am).
const QUIET_HOURS: number[] = Array.from({ length: 24 }, (_, i) => i);

export default function NotificationsScreen() {
  const router = useRouter();
  const db = useSQLiteContext();

  const [permission, setPermission] = useState<NotificationPermissionStatus>('not-requested');
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [frequency, setFrequency] = useState<NotificationFrequency>('weekly');
  const [weekday, setWeekday] = useState(2);
  const [hour, setHour] = useState(8);
  const [notifyOverdue, setNotifyOverdue] = useState(true);
  const [notifyTaskReminders, setNotifyTaskReminders] = useState(true);
  const [notifySystemReminders, setNotifySystemReminders] = useState(true);
  const [notifySound, setNotifySound] = useState(true);
  const [quietStart, setQuietStart] = useState<number | null>(null);
  const [quietEnd, setQuietEnd] = useState<number | null>(null);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [homeCount, setHomeCount] = useState(1);
  const [homeName, setHomeName] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // Re-check permission on focus — if the user toggled it in the OS
  // Settings app and came back, we want the badge to update without
  // a manual refresh.
  const refreshPermission = useCallback(async () => {
    setPermissionLoading(true);
    try {
      const s = await getNotificationPermissionStatus();
      setPermission(s);
    } finally {
      setPermissionLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void refreshPermission();
    }, [refreshPermission])
  );

  useEffect(() => {
    (async () => {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      // Multi-home awareness: preferences on this page belong to the
      // ACTIVE home; every home gets its own digest schedule.
      const countRow = await db.getFirstAsync<{ n: number }>(
        `SELECT COUNT(*) AS n FROM property WHERE deleted_at IS NULL`
      );
      setHomeCount(countRow?.n ?? 1);
      setHomeName(prop.name?.trim() || null);
      setPropertyId(prop.id);
      setEnabled((prop.notifications_enabled ?? 1) === 1);
      setFrequency(
        prop.notification_frequency
          ?? (prop.notifications_enabled === 1 ? 'weekly' : 'off')
      );
      setHour(prop.notification_hour ?? 8);
      setWeekday((prop.notification_day ?? 1) + 1);
      setNotifyOverdue((prop.notify_overdue ?? 1) === 1);
      setNotifyTaskReminders((prop.notify_task_reminders ?? 1) === 1);
      setNotifySystemReminders((prop.notify_system_reminders ?? 1) === 1);
      setNotifySound((prop.notify_sound ?? 1) === 1);
      setQuietStart(prop.quiet_hours_start ?? null);
      setQuietEnd(prop.quiet_hours_end ?? null);
    })();
  }, [db]);

  const persist = async (
    newEnabled: boolean,
    newFreq: NotificationFrequency,
    newWeekday: number,
    newHour: number,
    newOverdue: boolean
  ) => {
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET
         notifications_enabled = ?,
         notification_frequency = ?,
         notification_day = ?,
         notification_hour = ?,
         notify_overdue = ?,
         updated_at = ?
       WHERE id = ?`,
      newEnabled ? 1 : 0,
      newFreq,
      newWeekday - 1,
      newHour,
      newOverdue ? 1 : 0,
      now(),
      propertyId
    );
  };

  // The unified composer reads the just-persisted preferences straight
  // from the DB, so every handler is simply: persist → recompose. It
  // handles off/disabled (cancel), missing permission (no-op), monthly
  // vs weekly vs daily triggers, and the overdue-nudge refresh.
  const reschedule = async () => {
    await composeAndScheduleDigest(db);
  };

  // ── Permission management ─────────────────────────────────────────
  const onRequestPermission = async () => {
    const next = await requestNotificationPermissionUnified();
    setPermission(next);
    if (next === 'granted') {
      setStatus("Notifications turned on. We'll keep them gentle.");
      // Re-apply scheduling now that we have permission.
      await reschedule();
      // Confirmation buzz so the user sees notifications actually work.
      // Different APIs per platform — web uses the SW path, native uses
      // expo-notifications with a null trigger (immediate fire).
      if (Platform.OS === 'web') {
        await showImmediateWebNotification(
          'Homestead — notifications on',
          "We'll send the occasional nudge. Nothing naggy.",
          '/'
        );
      } else {
        // Sound toggle hasn't been loaded into state on a fresh
        // permission grant; default to "with sound" since the user
        // is in the act of opting in.
        await fireConfirmationNotification(notifySound);
      }
    } else if (next === 'denied') {
      setStatus(
        Platform.OS === 'web'
          ? 'Your browser blocked notifications. Click the lock icon in the address bar to re-enable.'
          : "iOS / Android blocked notifications. Open your device Settings → Homestead → Notifications to enable."
      );
    }
  };

  const openOsSettings = async () => {
    if (Platform.OS === 'web') return;
    try {
      await Linking.openSettings();
    } catch {
      // Some launchers block this; nothing we can do but tell the user.
    }
  };

  // ── Setting changes ───────────────────────────────────────────────
  const onEnabledToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    await persist(next, frequency, weekday, hour, notifyOverdue);
    await reschedule();
    setStatus(next ? 'Notifications on.' : 'All notifications paused.');
  };

  const onFreq = async (v: NotificationFrequency) => {
    setFrequency(v);
    await persist(enabled, v, weekday, hour, notifyOverdue);
    await reschedule();
  };

  const onWeekday = async (d: number) => {
    setWeekday(d);
    await persist(enabled, frequency, d, hour, notifyOverdue);
    await reschedule();
  };

  const onHour = async (h: number) => {
    setHour(h);
    await persist(enabled, frequency, weekday, h, notifyOverdue);
    await reschedule();
  };

  const onOverdue = async () => {
    const v = !notifyOverdue;
    setNotifyOverdue(v);
    await persist(enabled, frequency, weekday, hour, v);
    await reschedule();
  };

  const onTaskReminders = async () => {
    const v = !notifyTaskReminders;
    setNotifyTaskReminders(v);
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET notify_task_reminders = ?, updated_at = ? WHERE id = ?`,
      v ? 1 : 0,
      now(),
      propertyId
    );
    // Honoring "off" means actually canceling already-scheduled
    // reminders, not just gating future ones. Otherwise the user
    // thinks they're off, then a reminder still fires from the OS queue.
    if (!v) {
      await cancelAllTaskReminders();
      setStatus(
        'All upcoming task reminders cancelled. Re-enable any time on a task’s detail page.'
      );
    } else {
      setStatus(
        'Task reminders on. Set one on any task’s detail page when you want one.'
      );
    }
  };

  const onSystemReminders = async () => {
    const v = !notifySystemReminders;
    setNotifySystemReminders(v);
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET notify_system_reminders = ?, updated_at = ? WHERE id = ?`,
      v ? 1 : 0,
      now(),
      propertyId
    );
    if (!v) {
      await cancelAllSystemReminders();
      setStatus(
        'System reminders paused. Dates stay saved — re-enable any time.'
      );
    } else {
      setStatus('System reminders on. Save a system to schedule its reminder.');
    }
  };

  const onSound = async () => {
    const v = !notifySound;
    setNotifySound(v);
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET notify_sound = ?, updated_at = ? WHERE id = ?`,
      v ? 1 : 0,
      now(),
      propertyId
    );
    setStatus(v ? 'Sound on.' : 'Sound off. Notifications still visible.');
  };

  const onQuietToggle = async () => {
    // Off when both bounds are null. Tapping "on" defaults to a
    // sensible 10pm–7am window the user can adjust below.
    const goingOn = quietStart == null;
    const newStart = goingOn ? 22 : null;
    const newEnd = goingOn ? 7 : null;
    setQuietStart(newStart);
    setQuietEnd(newEnd);
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET quiet_hours_start = ?, quiet_hours_end = ?, updated_at = ? WHERE id = ?`,
      newStart,
      newEnd,
      now(),
      propertyId
    );
    setStatus(
      goingOn
        ? 'Quiet hours on (10 PM–7 AM). Adjust below if you like.'
        : 'Quiet hours off. We can ping you any time.'
    );
  };

  const onQuietStart = async (h: number) => {
    setQuietStart(h);
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET quiet_hours_start = ?, updated_at = ? WHERE id = ?`,
      h,
      now(),
      propertyId
    );
  };

  const onQuietEnd = async (h: number) => {
    setQuietEnd(h);
    if (!propertyId) return;
    await db.runAsync(
      `UPDATE property SET quiet_hours_end = ?, updated_at = ? WHERE id = ?`,
      h,
      now(),
      propertyId
    );
  };

  // Manual confirmation fire — lets the user verify the path is wired
  // without waiting for a scheduled digest. Honors the sound master so
  // the user can hear whether their "Sound off" toggle is wired up,
  // and warns them if they tap during quiet hours since the fire is
  // intentionally suppressed in that window.
  const onSendTest = async () => {
    if (permission !== 'granted') {
      setStatus('Grant permission first, then try again.');
      return;
    }
    if (isInQuietHours(quietStart, quietEnd)) {
      setStatus(
        "You're inside quiet hours. We'd suppress this in real use — toggle quiet hours off below to test."
      );
      return;
    }
    if (Platform.OS === 'web') {
      const ok = await showImmediateWebNotification(
        'Homestead — test notification',
        'This is what a notification from Homestead looks like.',
        '/'
      );
      setStatus(ok ? 'Test notification sent. Check your notification center.' : "Couldn't fire — check browser permission.");
    } else {
      await fireConfirmationNotification(notifySound);
      setStatus(
        notifySound
          ? 'Test notification sent. Check the top of your screen.'
          : 'Test notification sent silently. Sound is off in your settings.'
      );
    }
  };

  const showSchedule = enabled && permission === 'granted' && NOTIFICATIONS_AVAILABLE;
  const showWebInfo = Platform.OS === 'web';

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <View style={styles.topbarLeft}>
          <Pressable
            onPress={() => goBackOrTo(router, '/(tabs)/settings')}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Home"
            onPress={() => goTo(router, '/(tabs)')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <HomeIcon size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
          <Pressable
            accessibilityLabel="Go to Schedule"
            onPress={() => goTo(router, '/(tabs)/schedule')}
            style={({ pressed }) => [styles.shortcutBtn, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <Calendar size={18} color={Palette.forest} strokeWidth={1.9} />
          </Pressable>
        </View>
        <Text style={styles.topbarTitle}>Notifications</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={styles.h1}>How should we nudge you?</Text>
          <Text style={styles.sub}>
            Gentle, opt-in, never naggy. Turn on the permission once, then
            pick what kind of pings you actually want.
          </Text>
        </View>

        <PermissionCard
          permission={permission}
          loading={permissionLoading}
          onRequest={onRequestPermission}
          onOpenSettings={openOsSettings}
        />

        {homeCount > 1 ? (
          <View style={styles.infoCard}>
            <HomeIcon size={14} color={Palette.honeyDeep} strokeWidth={2} />
            <Text style={styles.infoText}>
              You&apos;re editing the schedule for{' '}
              <Text style={{ fontFamily: Fonts.sansBold }}>
                {homeName ?? 'this home'}
              </Text>
              . Every home gets its own digest on its own rhythm — switch
              homes from My Home to adjust another one.
            </Text>
          </View>
        ) : null}

        {showWebInfo ? (
          <View style={styles.infoCard}>
            <Info size={14} color={Palette.honeyDeep} strokeWidth={2} />
            <Text style={styles.infoText}>
              On web, Homestead can send notifications while the app is
              open (achievement unlocks, immediate alerts). Daily / weekly
              digests need iOS or Android — those use scheduled local
              notifications that browsers can&apos;t fire in the
              background without server-side push.
            </Text>
          </View>
        ) : null}

        <Text style={styles.section}>MASTER SWITCH</Text>
        <Pressable
          onPress={onEnabledToggle}
          accessibilityLabel="Toggle all notifications"
          style={({ pressed }) => [styles.card, pressed && { opacity: 0.96 }]}>
          <View style={styles.optRow}>
            <View style={[styles.iconBox, enabled && styles.iconBoxOn]}>
              {enabled ? (
                <Bell size={18} color={Palette.forest} strokeWidth={1.9} />
              ) : (
                <BellOff size={18} color={Palette.inkSoft} strokeWidth={1.9} />
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.optLabel}>
                {enabled ? 'Notifications on' : 'All notifications paused'}
              </Text>
              <Text style={styles.optSub}>
                {enabled
                  ? "We'll only ping you for the things below."
                  : 'Everything is silenced until you turn this back on.'}
              </Text>
            </View>
            <View style={[styles.toggle, enabled && styles.toggleOn]}>
              <View style={[styles.toggleThumb, enabled && styles.toggleThumbOn]} />
            </View>
          </View>
        </Pressable>

        {enabled ? (
          <>
            {/* Digest controls — native-only. On web we hide them
                because browsers can't background-schedule. */}
            {NOTIFICATIONS_AVAILABLE ? (
              <>
                <Text style={styles.section}>DIGEST FREQUENCY</Text>
                <View style={styles.card}>
                  {FREQ_OPTIONS.map((o, i) => {
                    const sel = frequency === o.v;
                    return (
                      <Pressable
                        key={o.v}
                        onPress={() => onFreq(o.v)}
                        style={({ pressed }) => [
                          styles.optRow,
                          i < FREQ_OPTIONS.length - 1 && styles.optRowBorder,
                          pressed && { opacity: 0.92 },
                        ]}>
                        <View style={[styles.radio, sel && styles.radioSel]}>
                          {sel ? <View style={styles.radioDot} /> : null}
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.optLabel}>{o.label}</Text>
                          <Text style={styles.optSub}>{o.sub}</Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              </>
            ) : null}

            {showSchedule && frequency !== 'off' ? (
              <>
                {frequency === 'weekly' ? (
                  <>
                    <Text style={styles.section}>DAY OF WEEK</Text>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      style={{ paddingHorizontal: Spacing.four }}>
                      {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                        const sel = weekday === d;
                        return (
                          <Pressable
                            key={d}
                            onPress={() => onWeekday(d)}
                            style={({ pressed }) => [
                              styles.chip,
                              sel && styles.chipSel,
                              pressed && { opacity: 0.9 },
                            ]}>
                            <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                              {dayLabel(d)}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </ScrollView>
                  </>
                ) : null}

                <Text style={styles.section}>TIME OF DAY</Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  style={{ paddingHorizontal: Spacing.four }}>
                  {[6, 7, 8, 9, 10, 12, 17, 18, 19, 20].map((h) => {
                    const sel = hour === h;
                    return (
                      <Pressable
                        key={h}
                        onPress={() => onHour(h)}
                        style={({ pressed }) => [
                          styles.chip,
                          sel && styles.chipSel,
                          pressed && { opacity: 0.9 },
                        ]}>
                        <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                          {formatHour(h)}
                        </Text>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </>
            ) : null}

            {/* What you'll get notified about — each row is a real
                opt-out toggle. Copy reassures the user that turning
                something off doesn't lose information; the inbox and
                home screen always carry the record. */}
            {/* Achievement-unlock pings were removed from this list in
                Wave 46: the watcher behind them is currently disabled,
                and a toggle that controls nothing erodes trust in every
                other toggle on the page. Re-add when the watcher ships. */}
            <Text style={styles.section}>WHAT TO PING YOU FOR</Text>
            <View style={styles.card}>
              <Pressable
                onPress={onOverdue}
                style={({ pressed }) => [
                  styles.optRow,
                  pressed && { opacity: 0.92 },
                ]}>
                <View style={[styles.iconBox, styles.iconBoxClay]}>
                  <TriangleAlert size={16} color={Palette.clay} strokeWidth={1.9} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.optLabel}>Overdue nudges</Text>
                  <Text style={styles.optSub}>
                    {notifyOverdue
                      ? "One soft ping if a task has been waiting. Never red, never stacked."
                      : 'Off. Overdue tasks still show up on your home screen.'}
                  </Text>
                </View>
                <View style={[styles.toggle, notifyOverdue && styles.toggleOn]}>
                  <View style={[styles.toggleThumb, notifyOverdue && styles.toggleThumbOn]} />
                </View>
              </Pressable>
            </View>

            {/* Reassurance footer — the inbox is the durable record. */}
            <View style={styles.reassureCard}>
              <Info size={13} color={Palette.forest} strokeWidth={2} />
              <Text style={styles.reassureText}>
                Turning a category off doesn&apos;t lose anything. Your inbox
                keeps the full history, and the home screen always shows
                what&apos;s next.
              </Text>
            </View>

            {/* Per-item reminder global gates. Per-task and per-system
                reminders are SET on their respective detail pages but
                CAN BE PAUSED globally here without losing the saved
                lead times / dates. Turning back on re-arms future
                scheduling; the user re-touches a task or system to
                re-arm existing ones. */}
            <Text style={styles.section}>PER-ITEM REMINDERS</Text>
            <View style={styles.card}>
              <Pressable
                onPress={onTaskReminders}
                style={({ pressed }) => [
                  styles.optRow,
                  styles.optRowBorder,
                  pressed && { opacity: 0.92 },
                ]}>
                <View style={[styles.iconBox]}>
                  <BellRing size={16} color={Palette.forest} strokeWidth={1.9} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.optLabel}>Task reminders</Text>
                  <Text style={styles.optSub}>
                    {notifyTaskReminders
                      ? "Per-task pings 1 or 7 days before they're due. Set on each task's detail page."
                      : 'Paused. Tasks still appear in Schedule — we just won’t buzz you.'}
                  </Text>
                </View>
                <View style={[styles.toggle, notifyTaskReminders && styles.toggleOn]}>
                  <View style={[styles.toggleThumb, notifyTaskReminders && styles.toggleThumbOn]} />
                </View>
              </Pressable>
              <Pressable
                onPress={onSystemReminders}
                style={({ pressed }) => [styles.optRow, pressed && { opacity: 0.92 }]}>
                <View style={[styles.iconBox]}>
                  <Wrench size={16} color={Palette.forest} strokeWidth={1.9} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.optLabel}>System reminders</Text>
                  <Text style={styles.optSub}>
                    {notifySystemReminders
                      ? "Warranty renewals, service contracts, anything dated. Set on each system's page."
                      : 'Paused. Dates stay saved on each system. We just don’t buzz you when they hit.'}
                  </Text>
                </View>
                <View style={[styles.toggle, notifySystemReminders && styles.toggleOn]}>
                  <View style={[styles.toggleThumb, notifySystemReminders && styles.toggleThumbOn]} />
                </View>
              </Pressable>
            </View>

            {/* Quiet hours — a single contiguous window across midnight
                is supported (e.g. 22 → 7). Affects immediate fires
                (achievement, test). Scheduled digest/per-task fires
                already happen at user-chosen hours, so we don't
                second-guess those. */}
            <Text style={styles.section}>QUIET HOURS</Text>
            <Pressable
              onPress={onQuietToggle}
              style={({ pressed }) => [styles.card, pressed && { opacity: 0.96 }]}>
              <View style={[styles.optRow, quietStart != null && styles.optRowBorder]}>
                <View style={[styles.iconBox]}>
                  <Moon size={16} color={Palette.forest} strokeWidth={1.9} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.optLabel}>
                    {quietStart != null ? 'Quiet hours on' : 'Quiet hours off'}
                  </Text>
                  <Text style={styles.optSub}>
                    {quietStart != null
                      ? `${formatHour(quietStart)} – ${formatHour(quietEnd ?? 7)}. We suppress immediate pings during this window.`
                      : "We can ping you any time. Inbox always carries the record."}
                  </Text>
                </View>
                <View style={[styles.toggle, quietStart != null && styles.toggleOn]}>
                  <View style={[styles.toggleThumb, quietStart != null && styles.toggleThumbOn]} />
                </View>
              </View>
              {quietStart != null ? (
                <View style={styles.quietPickers}>
                  <View style={styles.quietPickerRow}>
                    <Text style={styles.quietPickerLabel}>FROM</Text>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={styles.quietHourStrip}>
                      {QUIET_HOURS.map((h) => {
                        const sel = quietStart === h;
                        return (
                          <Pressable
                            key={`s-${h}`}
                            onPress={() => onQuietStart(h)}
                            style={({ pressed }) => [
                              styles.chip,
                              sel && styles.chipSel,
                              pressed && { opacity: 0.9 },
                            ]}>
                            <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                              {formatHour(h)}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </ScrollView>
                  </View>
                  <View style={styles.quietPickerRow}>
                    <Text style={styles.quietPickerLabel}>UNTIL</Text>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={styles.quietHourStrip}>
                      {QUIET_HOURS.map((h) => {
                        const sel = quietEnd === h;
                        return (
                          <Pressable
                            key={`e-${h}`}
                            onPress={() => onQuietEnd(h)}
                            style={({ pressed }) => [
                              styles.chip,
                              sel && styles.chipSel,
                              pressed && { opacity: 0.9 },
                            ]}>
                            <Text style={[styles.chipText, sel && styles.chipTextSel]}>
                              {formatHour(h)}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </ScrollView>
                  </View>
                </View>
              ) : null}
            </Pressable>

            {/* Sound master — affects only immediate fires today.
                Scheduled fires (digest, per-task reminders) still use
                whatever sound preference Expo's local notifications
                default to; that's a per-platform concern we don't try
                to override at scheduling time. */}
            <Text style={styles.section}>SOUND</Text>
            <Pressable
              onPress={onSound}
              style={({ pressed }) => [styles.card, pressed && { opacity: 0.96 }]}>
              <View style={styles.optRow}>
                <View style={[styles.iconBox]}>
                  {notifySound ? (
                    <Volume2 size={16} color={Palette.forest} strokeWidth={1.9} />
                  ) : (
                    <VolumeX size={16} color={Palette.inkSoft} strokeWidth={1.9} />
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.optLabel}>
                    {notifySound ? 'Sound on' : 'Sound off'}
                  </Text>
                  <Text style={styles.optSub}>
                    {notifySound
                      ? 'Notifications use your default notification sound.'
                      : "Silent. You'll still see the notification banner, just no audio."}
                  </Text>
                </View>
                <View style={[styles.toggle, notifySound && styles.toggleOn]}>
                  <View style={[styles.toggleThumb, notifySound && styles.toggleThumbOn]} />
                </View>
              </View>
            </Pressable>

            {/* Per-task reminders live on the task detail page. Anchor
                here so users know where to look. */}
            <View style={styles.linkCard}>
              <View style={styles.linkIcon}>
                <BellRing size={14} color={Palette.forest} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.linkTitle}>Per-task reminders</Text>
                <Text style={styles.linkBody}>
                  Want a specific task to ping you 1 or 7 days before it&apos;s
                  due? Open the task and set the reminder on its detail page.
                </Text>
              </View>
            </View>

            {/* Test button — useful any time, not just right after
                granting permission. Reachable as long as permission is
                granted. */}
            {permission === 'granted' ? (
              <Pressable
                onPress={onSendTest}
                accessibilityLabel="Send a test notification"
                style={({ pressed }) => [styles.testBtn, pressed && { opacity: 0.85 }]}>
                <Bell size={15} color={Palette.forest} strokeWidth={2} />
                <Text style={styles.testBtnText}>Send a test notification</Text>
              </Pressable>
            ) : null}
          </>
        ) : null}

        {status ? (
          <View style={styles.status}>
            <Text style={styles.statusText}>{status}</Text>
          </View>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

// ── Permission card ────────────────────────────────────────────────────

function PermissionCard({
  permission,
  loading,
  onRequest,
  onOpenSettings,
}: {
  permission: NotificationPermissionStatus;
  loading: boolean;
  onRequest: () => void;
  onOpenSettings: () => void;
}) {
  const isGranted = permission === 'granted';
  const isDenied = permission === 'denied';
  const isUnavailable = permission === 'unavailable';
  const isNotRequested = permission === 'not-requested';

  return (
    <View style={[styles.permCard, isGranted && styles.permCardGranted]}>
      <View
        style={[
          styles.permIcon,
          isGranted && styles.permIconGranted,
          isDenied && styles.permIconDenied,
        ]}>
        {isGranted ? (
          <Check size={20} color={Palette.cream} strokeWidth={2.4} />
        ) : isDenied ? (
          <X size={20} color={Palette.clay} strokeWidth={2.4} />
        ) : isUnavailable ? (
          <BellOff size={20} color={Palette.inkSoft} strokeWidth={1.9} />
        ) : (
          <Bell size={20} color={Palette.forest} strokeWidth={1.9} />
        )}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[styles.permTitle, isGranted && styles.permTitleGranted]}>
          {loading
            ? 'Checking permission…'
            : isGranted
            ? 'Push notifications enabled'
            : isDenied
            ? 'Permission blocked'
            : isUnavailable
            ? 'Not available here'
            : 'Push notifications are off'}
        </Text>
        <Text style={[styles.permBody, isGranted && styles.permBodyGranted]}>
          {loading
            ? ' '
            : isGranted
            ? Platform.OS === 'web'
              ? "Your browser will surface alerts while Homestead is open. We'll never spam."
              : "Your phone will surface alerts. We'll only ping for what you turn on below."
            : isDenied
            ? Platform.OS === 'web'
              ? 'Notifications are blocked at the browser level. Click the lock icon in the address bar → Notifications → Allow.'
              : 'iOS / Android blocked notifications. Open device Settings → Homestead → Notifications to re-enable.'
            : isUnavailable
            ? "This browser doesn't support web notifications. Open Homestead on iOS, Android, or a different browser."
            : Platform.OS === 'web'
            ? "Tap below to let your browser show Homestead notifications. We'll only ping for what you turn on."
            : 'Tap below to let your phone show Homestead notifications.'}
        </Text>
        {isNotRequested && !isUnavailable ? (
          <Pressable
            onPress={onRequest}
            accessibilityLabel="Enable push notifications"
            style={({ pressed }) => [styles.permBtn, pressed && { transform: [{ scale: 0.98 }] }]}>
            <Bell size={15} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.permBtnText}>Enable notifications</Text>
          </Pressable>
        ) : isDenied && Platform.OS !== 'web' ? (
          <Pressable
            onPress={onOpenSettings}
            accessibilityLabel="Open device settings"
            style={({ pressed }) => [styles.permBtnOutline, pressed && { opacity: 0.85 }]}>
            <SettingsIcon size={15} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.permBtnOutlineText}>Open device settings</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  topbarLeft: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  shortcutBtn: {
    width: 32, height: 32, borderRadius: 16, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: {
    fontFamily: Fonts.serif, fontSize: 27, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginTop: 4, fontSize: 13, lineHeight: 19,
    color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },

  // Permission card — top-of-page anchor. Border + bg shift color based
  // on state so a glance tells you whether you're set or blocked.
  permCard: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  permCardGranted: {
    backgroundColor: '#E0EAD9',
    borderColor: '#C0D4B6',
  },
  permIcon: {
    width: 38, height: 38, borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
    marginTop: 2,
  },
  permIconGranted: { backgroundColor: Palette.moss },
  permIconDenied: { backgroundColor: 'rgba(176,95,60,0.18)' },
  permTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 15,
    color: Palette.ink,
    letterSpacing: -0.2,
  },
  permTitleGranted: { color: '#2D4928' },
  permBody: {
    marginTop: 4,
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 18,
  },
  permBodyGranted: { color: '#3D5938' },
  permBtn: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 11,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: Palette.forest,
    alignSelf: 'flex-start',
  },
  permBtnText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream },
  permBtnOutline: {
    marginTop: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.forest,
    backgroundColor: Palette.card,
    alignSelf: 'flex-start',
  },
  permBtnOutlineText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },

  infoCard: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: Palette.honeySoft,
    borderWidth: 1,
    borderColor: 'rgba(225,163,61,0.4)',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 9,
  },
  infoText: {
    flex: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: '#5A4012',
    lineHeight: 16,
  },

  section: {
    marginTop: Spacing.five, marginHorizontal: Spacing.four,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.7, marginBottom: 8,
  },
  card: {
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingHorizontal: 16,
  },
  optRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 13 },
  optRowBorder: { borderBottomWidth: 1, borderBottomColor: Palette.line },
  radio: {
    width: 22, height: 22, borderRadius: 11,
    borderWidth: 2, borderColor: Palette.sandDeep,
    alignItems: 'center', justifyContent: 'center',
  },
  radioSel: { borderColor: Palette.forest },
  radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: Palette.forest },
  optLabel: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  optSub: {
    fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkSoft, marginTop: 2, lineHeight: 17,
  },
  iconBox: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  iconBoxOn: { backgroundColor: '#DCE7E1' },
  iconBoxClay: { backgroundColor: 'rgba(176,95,60,0.13)' },
  iconBoxHoney: { backgroundColor: '#F3DDAE' },
  toggle: {
    width: 44, height: 26, borderRadius: 13,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleLocked: { opacity: 0.5 },
  toggleThumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 18 }] },

  // Quiet-hours start/end picker rows. Sits inside the same card as
  // the quiet-hours toggle so the visual relationship reads "this is
  // the section the toggle controls" rather than two separate things.
  quietPickers: {
    paddingBottom: 14,
  },
  quietPickerRow: {
    paddingTop: 12,
  },
  quietPickerLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.7,
    marginBottom: 7,
  },
  quietHourStrip: {
    paddingRight: 8,
  },
  chip: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999,
    borderWidth: 1.5, borderColor: Palette.line, backgroundColor: Palette.card,
    marginRight: 6,
  },
  chipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  chipText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  chipTextSel: { color: Palette.cream },

  linkCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 11,
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
  },
  linkIcon: {
    width: 28, height: 28, borderRadius: 9,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  linkTitle: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  linkBody: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkSoft, lineHeight: 17,
  },

  // Reassurance footer for the "what to ping you for" section. Quiet
  // visual treatment — line border, small text, info icon — so it
  // doesn't compete with the toggles above. The job is to tell the
  // user "you can't accidentally lose anything by turning these off."
  reassureCard: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 13,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 9,
  },
  reassureText: {
    flex: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 16,
  },

  testBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.forest,
    backgroundColor: Palette.card,
  },
  testBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
  },

  status: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    padding: 13, borderRadius: 12, backgroundColor: Palette.forest,
  },
  statusText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 13.5 },
});
